import { Injectable, ApplicationRef } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { defer, of, combineLatest } from 'rxjs';
import {
  AppInit,
  AppAuthenticated,
  AppUnauthenticated,
  AppIsStable,
  AppIsUnstable,
  AppIsReady,
  AppStartNavigate,
  AppStopNavigate,
  AppNavigateTo
} from './life-cycle.actions';
import {
  SetUser,
  UserActionTypes
} from '../authentication/actions/user.actions';
import { filter, map, first, distinctUntilChanged, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { isAppReady } from './life-cycle.selectors';
import {
  Router,
  NavigationStart,
  NavigationEnd,
  NavigationCancel
} from '@angular/router';
import { ConfigService } from '../config/config.service';

@Injectable()
export class LifeCycleEffects {
  @Effect()
  public appInit$ = defer(() => of(new AppInit()));

  @Effect()
  public appAuthenticated$ = combineLatest(
    this.actions$.pipe(
      ofType<SetUser>(UserActionTypes.SetUser),
      filter(action => !!action.user)
    ),
    this.config.authIsEnabled$
  ).pipe(map(() => new AppAuthenticated()));

  @Effect()
  public appUnauthenticated$ = combineLatest(
    this.actions$.pipe(
      ofType<SetUser>(UserActionTypes.SetUser),
      filter(action => action.user === false)
    ),
    this.config.authIsEnabled$
  ).pipe(map(() => new AppUnauthenticated()));

  @Effect()
  public appIsStable$ = defer(() =>
    this.appRef.isStable.pipe(
      filter(Boolean),
      distinctUntilChanged(),
      map(() => new AppIsStable())
    )
  );

  @Effect({ dispatch: false })
  public navigateTo = this.actions$.pipe(
    ofType<AppNavigateTo>(AppNavigateTo.TYPE),
    tap(action => this.router.navigate(action.commands, action.extras))
  );

  @Effect()
  public appIsUnstable$ = defer(() =>
    this.appRef.isStable.pipe(
      filter(isStable => !isStable),
      distinctUntilChanged(),
      map(() => new AppIsUnstable())
    )
  );

  @Effect()
  public appIsReady$ = this.store$.select(isAppReady).pipe(
    filter(Boolean),
    first(),
    map(() => new AppIsReady())
  );

  @Effect()
  public navigate$ = this.router.events.pipe(
    filter(
      event =>
        event instanceof NavigationStart ||
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel
    ),
    map(event => event instanceof NavigationStart),
    map(started => (started ? new AppStartNavigate() : new AppStopNavigate()))
  );

  constructor(
    public actions$: Actions,
    public appRef: ApplicationRef,
    public logger: NGXLogger,
    public router: Router,
    public config: ConfigService,
    public store$: Store<any>
  ) {}
}
